/**
 * Звонок в реплике (изменение v1.14).
 *
 * Снимки `call.updated` лежат в store `calls`: по ним любая вкладка видит входящий вызов и
 * идущий звонок одинаково, даже если WebSocket держит другая вкладка. Ключей, SDP и адресов
 * в реплике нет — их знает только вкладка, которая ведёт звонок.
 */
import type { Call } from '@shared/api/types'
import { callsRepo, useReplicaList } from '@shared/db'

export type { Call }

/** Незавершённые звонки аккаунта в порядке создания. */
export function useOpenCalls(): Call[] {
  const { items } = useReplicaList(async () => {
    const calls = await callsRepo.open()
    return calls.sort((a, b) => a.created_at.localeCompare(b.created_at))
  }, [])
  return items
}

/** Входящий вызов, на который ещё можно ответить. */
export function incomingCallOf(calls: readonly Call[], now = Date.now()): Call | null {
  for (const call of calls) {
    if (call.role === 'callee' && call.state === 'ringing' && Date.parse(call.ringing_until) > now)
      return call
  }
  return null
}

/**
 * Звонок, закреплённый за этим устройством и ещё не завершённый: либо он ведётся в одной из
 * вкладок, либо осиротел после перезагрузки — это решает владелец Web Lock.
 */
export function deviceCallOf(calls: readonly Call[], deviceId: string | null | undefined): Call | null {
  if (!deviceId) return null
  for (const call of calls) {
    if (call.device_id !== deviceId) continue
    if (call.state === 'accepted' || call.state === 'active') return call
    if (call.state === 'ringing' && call.role === 'caller') return call
  }
  return null
}

/** Звонок принят другим устройством этого же аккаунта. */
export function answeredElsewhere(call: Call, deviceId: string | null | undefined): boolean {
  return (
    call.role === 'callee' &&
    call.state !== 'ringing' &&
    call.device_id !== null &&
    call.device_id !== deviceId
  )
}
